/**
 * Pure URL-parameter parsing for the client. Every `?param=` this package reads is
 * centralized here — including ones previously read ad hoc elsewhere (see each
 * field's comment for its previous home) — so there's one place that owns range
 * validation and defaulting. Every field is validated: an absent, empty, or
 * out-of-range value is silently ignored (falls back to `undefined`, or `false` for
 * the boolean flags) rather than throwing or producing `NaN` — the caller applies
 * its own default via `??`.
 *
 * Pure: takes a query string in (`URLSearchParams`-compatible), never touches
 * `window` itself, so it's testable without a DOM — same pattern as `./url-guard`.
 */

export interface ScareMazeParams {
  /** 0..1. Overrides `StepInput.focusThreshold` (default `FOCUS_THRESHOLD_DEFAULT`, src/focus/gate.ts). */
  readonly scareThreshold: number | undefined;
  /** 0..0.9. Overrides `steerFromGaze`'s `SteerConfig.deadzone` (default 0.08, src/input/steer.ts). */
  readonly deadzone: number | undefined;
  /** 0.1..10. Extra multiplier passed to `applyCalibration`'s `opts.gain` (default 1 when unset). */
  readonly gain: number | undefined;
  /** 0.05..10. Overrides `OneEuro2D`'s `OneEuroConfig.minCutoff` (default 1.0). */
  readonly minCutoff: number | undefined;
  /** `?invertX=1`. Passed to `applyCalibration`'s `opts.invertX`. */
  readonly invertX: boolean;
  /** `?invertY=1`. Passed to `applyCalibration`'s `opts.invertY`. */
  readonly invertY: boolean;
  /** `?delegate=gpu`. Previously read directly off `window.location.search` inside
   * `client/face/landmarker.ts`; now read here and passed into `createLandmarker`. */
  readonly delegate: "cpu" | "gpu";
  /** `?reducedMotion=1`. Previously read inline in `client/main.ts`'s `onAccept`. */
  readonly reducedMotion: boolean;
  /** `?audioUrl=`. Previously read inline in `client/main.ts`'s `resolveAudioUrl`; the
   * same-origin validation itself (`./url-guard`'s `sameOriginUrl`) still happens in
   * `client/main.ts`, since that needs `window.location.href` as the base and this
   * module stays DOM-free. Raw/unvalidated here — the caller must still validate it. */
  readonly audioUrl: string | undefined;
  /** 0..1. Overrides `AssistConfig.assistMax` (default 0.45, src/input/assist.ts). */
  readonly steerAssist: number | undefined;
  /** `?steer=0` disables the Jev steering-assist channel entirely (no `/api/steer`
   * polling, no blend). Default (absent, or any value other than the literal `0`) is
   * enabled — the inverse polarity of the other `?flag=1` params above, since this
   * one's "on" state is the default. */
  readonly steer: boolean;
  /** `?drift=0` disables calibration-drift correction between rounds. Same polarity as
   * `steer` above: enabled unless the literal `0`. */
  readonly drift: boolean;
  /** 200..5000. Overrides the steering channel's fast-cadence poll interval (default
   * 500ms, `client/steer/client.ts`'s `STEER_FAST_POLL_INTERVAL_MS_DEFAULT`). */
  readonly steerPoll: number | undefined;
  /** `?artBase=`. Overrides the base URL art (level backdrops, the wall texture) is
   * fetched from — see `client/render/art.ts`'s `resolveArtBaseUrl`. Raw/unvalidated
   * here (same convention as `audioUrl` above) — `resolveArtBaseUrl` itself is where
   * this gets sanity-checked (only an absolute http(s) URL or a root-relative path is
   * ever used; anything else, including absent, falls through to the build-time R2
   * constant or the same-origin `/art/` default). */
  readonly artBase: string | undefined;
}

function parseRangedFloat(raw: string | null, min: number, max: number): number | undefined {
  if (raw === null || raw.trim() === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < min || n > max) return undefined;
  return n;
}

function parseFlag(raw: string | null): boolean {
  return raw === "1";
}

/** Inverse polarity of `parseFlag`: true (enabled, the default) unless the param is
 * the literal string "0" — used for the `?steer=0`/`?drift=0` disable switches. */
function parseEnabledUnlessZero(raw: string | null): boolean {
  return raw !== "0";
}

/** `search` is a `location.search`-shaped string (leading `?` optional — `URLSearchParams` accepts both). */
export function parseParams(search: string): ScareMazeParams {
  const params = new URLSearchParams(search);
  return {
    scareThreshold: parseRangedFloat(params.get("scareThreshold"), 0, 1),
    deadzone: parseRangedFloat(params.get("deadzone"), 0, 0.9),
    gain: parseRangedFloat(params.get("gain"), 0.1, 10),
    minCutoff: parseRangedFloat(params.get("minCutoff"), 0.05, 10),
    invertX: parseFlag(params.get("invertX")),
    invertY: parseFlag(params.get("invertY")),
    delegate: params.get("delegate") === "gpu" ? "gpu" : "cpu",
    reducedMotion: parseFlag(params.get("reducedMotion")),
    audioUrl: params.get("audioUrl") ?? undefined,
    steerAssist: parseRangedFloat(params.get("steerAssist"), 0, 1),
    steer: parseEnabledUnlessZero(params.get("steer")),
    drift: parseEnabledUnlessZero(params.get("drift")),
    steerPoll: parseRangedFloat(params.get("steerPoll"), 200, 5000),
    artBase: params.get("artBase") ?? undefined,
  };
}
